import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SortDirection } from 'vidis-common';
import {
  CountPrescriptionResult,
  DeletePrescriptionsRequest,
  EditPrescriptionsRequest,
  Prescription,
  PrescriptionFilter,
  PrescriptionPagination,
  PrescriptionReservationRequest,
  PrescriptionResult
} from 'vidis-common/prescription';
import { MandatePermissions } from '../../models/enum/mandate-permissions.enum';
import { MandateService } from '../mandate/mandate.service';

@Injectable({
  providedIn: 'root'
})
export class PrescriptionsService {
  private readonly http = inject(HttpClient);
  private readonly mandateService = inject(MandateService);

  getPrescriptions(
    ssin?: string,
    filter?: PrescriptionFilter,
    pagination?: PrescriptionPagination
  ): Observable<PrescriptionResult> {
    return this.mandateService.hasAccess(MandatePermissions.consultPrescriptions).pipe(
      switchMap(hasAccess => {
        if (hasAccess) {
          const paginationForBody = {
            page: pagination?.page,
            pageSize: pagination?.pageSize,
            order: pagination?.order === SortDirection.ascending ? 'ascending' : 'descending',
            sort: pagination?.sort
          };
          let body = {
            ssins: ssin ? [ssin] : [],
            filter,
            pagination: paginationForBody,
          };
          return this.http.post<PrescriptionResult>('/v2/prescriptions/search', body);
        } else {
          return throwError(() => ({errors: [{code: 'no-access'}]}));
        }
      })
    );
  };

  countPrescriptions(
    ssin?: string
  ): Observable<CountPrescriptionResult> {
    return this.mandateService.hasAccess(MandatePermissions.consultPrescriptions).pipe(
      switchMap(hasAccess => {
        if (hasAccess) {
          let body = ssin ? [ssin] : [];
          return this.http.post<CountPrescriptionResult>('/v2/prescriptions/count', body);
        } else {
          return throwError(() => ({errors: [{code: 'no-access'}]}));
        }
      })
    );
  }

  getPrescription(ssin?: string, ridCode?: string): Observable<Prescription> {
    return this.mandateService.hasAccess(MandatePermissions.consultPrescriptions).pipe(
      switchMap(hasAccess => hasAccess
        ? this.http.post<Prescription>('/v2/prescriptions/findOne', {
          ssin: ssin,
          ridCode: ridCode
        })
        : throwError(() => ({errors: [{code: 'no-access'}]}))
      )
    );
  }

  editPrescriptions(body: EditPrescriptionsRequest): Observable<PrescriptionResult> {
    return this.http.patch<PrescriptionResult>('/v2/prescriptions/edit', body);
  }

  deletePrescriptions(body: DeletePrescriptionsRequest) {
    return this.http.delete('/v2/prescriptions/delete', {body});
  }

  makeReservationForPrescription(
    ridCode: string,
    reservationRequest: PrescriptionReservationRequest
  ) {
    return this.http.post(`/v1/openprescriptions/${ridCode}/reservation`, reservationRequest);
  }

  cancelReservationForPrescription(ridCode: string, ssin: string) {
    return this.http.delete(`/v1/openprescriptions/${ridCode}/reservation`, {
      body: {
        ssin: ssin,
      }
    });
  }
}
